import { useMemo } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronRight, Home, MessageCircle } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import useJsonLd from "@/hooks/useJsonLd";
import { Header, LeadForm, Footer, WHATSAPP_NUMBER } from "@/pages/Landing";
import { CAT_LABEL } from "@/pages/categories";
import { SEO_BY_SLUG, SEO_LABEL } from "@/pages/seoPages";

const easeOut = [0.2, 0.7, 0.2, 1];
const ALL_LABELS = { ...CAT_LABEL, ...SEO_LABEL };

function SeoFigure({ img, className = "", imgClass = "" }) {
  return (
    <figure className={className}>
      <img
        src={img.src}
        srcSet={img.srcSet}
        sizes="(max-width: 1024px) 100vw, 768px"
        width={img.width}
        height={img.height}
        alt={img.alt}
        loading="lazy"
        decoding="async"
        className={`w-full rounded-md border hairline object-cover ${imgClass || "h-auto"}`}
      />
      {img.caption && (
        <figcaption className="mt-2.5 text-[12px] leading-[1.6] text-white/40 italic">{img.caption}</figcaption>
      )}
    </figure>
  );
}

export default function SeoPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+/, "").split("/")[0] || "";
  const page = SEO_BY_SLUG[slug];
  const canonical = `https://ovamotorsparts.com/${slug}`;

  useSeo({ title: page?.title, description: page?.description, canonical, keywords: page?.keywords });

  const jsonLd = useMemo(() => {
    if (!page) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://ovamotorsparts.com/" },
            { "@type": "ListItem", position: 2, name: page.label, item: canonical },
          ],
        },
        {
          "@type": "Service",
          name: page.serviceName,
          provider: { "@id": "https://ovamotorsparts.com/#business" },
          areaServed: { "@type": "AdministrativeArea", name: "Guadeloupe" },
          url: canonical,
          description: page.description,
        },
        {
          "@type": "FAQPage",
          mainEntity: page.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    };
  }, [page, canonical]);
  useJsonLd(`ld-${slug}`, jsonLd);

  if (!page) return <Navigate to="/" replace />;

  return (
    <main data-testid={`seo-page-${page.slug}`} className="bg-[var(--ova-ink)] text-[var(--ova-off)] min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative pt-[104px] pb-14 lg:pb-20 border-b hairline">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <nav aria-label="breadcrumb" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/45 mb-8">
            <Link to="/" className="hover:text-[var(--ova-yellow)] inline-flex items-center gap-1.5"><Home size={12}/> Accueil</Link>
            <ChevronRight size={12} />
            <span className="text-white/80">{page.label} · Guadeloupe</span>
          </nav>

          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: easeOut }}>
            <div className="chapter mb-5">OVA Motors Parts · Guadeloupe (971)</div>
            <h1 className="font-display font-800 uppercase text-[38px] sm:text-[52px] lg:text-[72px] leading-[0.98] tracking-[-0.02em]">
              {page.h1}<span className="text-[var(--ova-yellow)]">.</span>
            </h1>
            <p className="mt-6 max-w-3xl font-display text-[18px] lg:text-[22px] text-white/80 leading-[1.5]">
              {page.hero}
            </p>
            <p className="mt-8 max-w-3xl text-[15px] leading-[1.75] text-white/60">
              {page.lead}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#demande" data-testid="seo-cta-form" className="btn-yellow px-7 py-4 text-[12px] uppercase tracking-[0.18em] inline-flex items-center gap-2">
                Demander cette pièce <ArrowUpRight size={14} />
              </a>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" data-testid="seo-cta-whatsapp" className="btn-ghost-light px-7 py-4 text-[12px] uppercase tracking-[0.18em] inline-flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#25D366]"><MessageCircle size={11} className="text-white" fill="white" strokeWidth={0} /></span>
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Grande photo de preuve après l'introduction */}
      {page.heroPhoto && (
        <section className="py-12 lg:py-16 border-b hairline">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.8, ease: easeOut }}
            >
              <SeoFigure img={page.heroPhoto} imgClass="max-h-[240px] sm:max-h-[300px] lg:max-h-[380px]" />
            </motion.div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-16 lg:py-24 border-b hairline">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-14">
            {page.sections.map((s, i) => (
              <motion.article
                key={s.h}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.7, ease: easeOut, delay: i * 0.06 }}
              >
                <div className="text-[var(--ova-yellow)] font-display font-800 text-[36px] leading-none tracking-tighter mb-4">0{i + 1}</div>
                <h2 className="font-display font-700 uppercase text-[24px] lg:text-[32px] tracking-tight mb-4">{s.h}</h2>
                {s.p && <p className="text-[15px] leading-[1.8] text-white/65">{s.p}</p>}
                {s.img && <SeoFigure img={s.img} className="mt-6 max-w-2xl" />}
                {s.subs && (
                  <div className="space-y-6">
                    {s.subs.map((sub) => (
                      <div key={sub.t}>
                        <h3 className="font-display font-700 text-[17px] uppercase tracking-tight text-white/90 mb-1.5">{sub.t}</h3>
                        <p className="text-[15px] leading-[1.8] text-white/65">{sub.p}</p>
                      </div>
                    ))}
                  </div>
                )}
              </motion.article>
            ))}

            {/* Engine codes (moteurs page — structure prête pour pages par code) */}
            {page.engineCodes && (
              <motion.article
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.7, ease: easeOut }}
                data-testid="engine-codes-block"
              >
                <div className="text-[var(--ova-yellow)] font-display font-800 text-[36px] leading-none tracking-tighter mb-4">0{page.sections.length + 1}</div>
                <h2 className="font-display font-700 uppercase text-[24px] lg:text-[32px] tracking-tight mb-4">{page.engineCodes.h}</h2>
                <p className="text-[15px] leading-[1.8] text-white/65 mb-6">{page.engineCodes.intro}</p>
                {page.engineCodes.img && <SeoFigure img={page.engineCodes.img} className="mb-6 max-w-md" />}
                <ul className="space-y-3">
                  {page.engineCodes.items.map((it) => (
                    <li key={it.code} className="card-dark px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <span className="font-display font-700 text-[16px] uppercase tracking-tight text-[var(--ova-yellow)]">{it.code}</span>
                      <span className="text-[13px] text-white/55">{it.vehicles}</span>
                      {it.href && (
                        <Link to={it.href} data-testid={`engine-code-${it.code}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-700 text-white/80 hover:text-[var(--ova-yellow)]">
                          Voir la page <ArrowUpRight size={13} />
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-[13px] leading-[1.7] text-white/45">{page.engineCodes.note}</p>
              </motion.article>
            )}

            {/* FAQ */}
            <div className="pt-10 border-t hairline" data-testid="faq-block">
              <h2 className="font-display font-700 uppercase text-[24px] lg:text-[32px] tracking-tight mb-8">Questions fréquentes</h2>
              <div className="space-y-7">
                {page.faq.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-display font-700 text-[17px] uppercase tracking-tight text-white/90 mb-2">{f.q}</h3>
                    <p className="text-[15px] leading-[1.8] text-white/65">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Maillage interne */}
            <div className="pt-10 border-t hairline">
              <h3 className="text-[11px] uppercase tracking-[0.28em] text-white/50 mb-5">Voir aussi</h3>
              <ul className="grid sm:grid-cols-2 gap-3">
                {page.related.map((rslug) => (
                  <li key={rslug}>
                    <Link
                      to={`/${rslug}`}
                      data-testid={`related-${rslug}`}
                      className="card-dark px-5 py-4 flex items-center justify-between text-[14px] font-600 hover:text-[var(--ova-yellow)] group"
                    >
                      <span>{ALL_LABELS[rslug] || rslug}</span>
                      <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform text-[var(--ova-yellow)]" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right rail — Lead form */}
          <aside className="lg:col-span-4" id="demande">
            <div className="lg:sticky lg:top-24">
              <LeadForm />
            </div>
          </aside>
        </div>
      </section>

      {/* Local zone note */}
      <section className="py-14 lg:py-20 bg-[var(--ova-panel)] border-b hairline">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="chapter mb-4">Zone d&apos;intervention</div>
          <h2 className="font-display font-700 uppercase text-[24px] lg:text-[36px] tracking-tight mb-4">
            Recherche & expédition depuis la <span className="text-[var(--ova-yellow)]">Guadeloupe</span>.
          </h2>
          <p className="max-w-3xl text-[14px] leading-[1.8] text-white/60">
            OVA Motors Parts est basé en Guadeloupe (971). Nous sourçons les pièces d&apos;occasion localement en priorité, puis auprès de notre réseau européen si nécessaire. Expédition organisée selon la pièce et la destination — nous vous confirmons les modalités au moment du devis.
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
